/**
 * Formulas API (owner: C4): formulas, versions, lines, normalized view, diff, scale,
 * change-dilution, cost, parse and share under /api/formulas/ (docs/mvp-build-spec.md §5).
 * Query keys start with "formulas". Decimals stay strings; fractions are 0–1.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/api/client";
import type { DecimalString, IsoDate, IsoDateTime, Paged, Uuid } from "@/api/types";
import type { StockState } from "@/lib/stock";

export type FormulaKind = "formula" | "accord" | "dilution";
export type VersionStatus = "draft" | "released" | "locked";
export type VersionSource = "manual" | "paste" | "import" | "trial_addition" | "ai" | "ladder" | "copy";
export type LineUnit = "pct" | "parts" | "g";
export type LineBasis = "neat" | "solution";
export type LineFlag = "none" | "too_strong" | "too_weak" | "highlight";

export interface MaterialBrief {
  id: Uuid | null;
  name: string;
  kind: string;
  short_code: string | null;
  is_solvent: boolean;
  cas: string | null;
}

export interface MaterialOption extends MaterialBrief {
  id: Uuid;
  owned: boolean;
  /** In stock, on order or not ordered (lots of its variants and merged duplicates count). */
  stock?: StockState;
  private: boolean;
}

export interface SubVersionBrief {
  id: Uuid;
  label: string;
  status: VersionStatus;
  formula_id: Uuid;
  formula_code: string;
  formula_name: string;
}

export interface LotBrief {
  id: Uuid;
  code: string;
  active_fraction: DecimalString;
  status: string;
}

export interface VersionBrief {
  id: Uuid;
  number: number;
  label: string;
  status: VersionStatus;
  source: VersionSource;
  parent_ids: Uuid[];
  content_hash: string;
  first_release_date: IsoDate | null;
  released_at: IsoDateTime | null;
  notes: string;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
  line_count: number;
}

export interface FormulaListItem {
  id: Uuid;
  code: string;
  name: string;
  kind: FormulaKind;
  tags: string[];
  family: string;
  project: string;
  archived: boolean;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
  version_count: number;
  latest_version: VersionBrief | null;
  latest_released_label: string | null;
  first_released_on: IsoDate | null;
}

export interface Formula {
  id: Uuid;
  code: string;
  name: string;
  kind: FormulaKind;
  tags: string[];
  family: string;
  project: string;
  notes: string;
  archived: boolean;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
  first_released_on: IsoDate | null;
  versions: VersionBrief[];
}

export interface FormulaLine {
  id: Uuid;
  position: number;
  material: MaterialBrief | null;
  sub_version: SubVersionBrief | null;
  preferred_lot: LotBrief | null;
  amount: DecimalString;
  unit: LineUnit;
  basis: LineBasis;
  active_fraction: DecimalString;
  carrier: MaterialBrief | null;
  notes: string;
  flag: LineFlag;
  owned: boolean | null;
  /** The workspace's stock of the line's material; null for an accord's line. */
  stock: StockState | null;
}

export interface FormulaVersion extends Omit<VersionBrief, "line_count"> {
  formula: { id: Uuid; code: string; name: string; kind: FormulaKind };
  created_by: string | null;
  lines: FormulaLine[];
}

/** A line as sent to the API (exactly one of material_id / sub_version_id). */
export interface LineInput {
  material_id?: Uuid | null;
  sub_version_id?: Uuid | null;
  amount: DecimalString;
  unit: LineUnit;
  basis?: LineBasis;
  active_fraction?: DecimalString;
  carrier_material_id?: Uuid | null;
  preferred_lot_id?: Uuid | null;
  notes?: string;
  flag?: LineFlag;
}

export type LinePatch = Partial<LineInput> & { position?: number };

export interface NormalizedRow {
  line_id: Uuid;
  position: number;
  kind: "material" | "accord";
  material: MaterialBrief | null;
  sub_version: SubVersionBrief | null;
  preferred_lot: LotBrief | null;
  amount: DecimalString;
  unit: LineUnit;
  basis: LineBasis;
  active_fraction: DecimalString;
  carrier: MaterialBrief | null;
  flag: LineFlag;
  notes: string;
  neat_fraction: DecimalString | null;
  weighed_fraction: DecimalString | null;
  weigh_g: DecimalString | null;
  neat_g: DecimalString | null;
  carried_g: DecimalString | null;
  owned: boolean | null;
}

export interface NormalizedView {
  version_id: Uuid;
  label: string;
  batch_g: DecimalString | null;
  concentration: DecimalString | null;
  error: string | null;
  warnings: string[];
  rows: NormalizedRow[];
  solvent_row: { material: MaterialBrief; weigh_g: DecimalString | null } | null;
  totals: {
    total_weigh_g: DecimalString | null;
    total_neat_g: DecimalString | null;
    batch_total_g: DecimalString | null;
  };
  carriers: { material: MaterialBrief; per_neat: DecimalString; grams: DecimalString | null }[];
  concentrate: {
    neat_share: DecimalString;
    diluents: { material: MaterialBrief; fraction: DecimalString }[];
    solvent_ratio: DecimalString;
    as_made_dosage: DecimalString | null;
  } | null;
  materials: {
    material: MaterialBrief;
    fraction: DecimalString;
    owned: boolean;
    contributions: { path: string; label: string; fraction: DecimalString }[];
  }[];
}

export interface PriceSource {
  kind: "lot" | "offer";
  label: string;
  price_minor: number;
  pack_g: DecimalString;
  active_fraction: DecimalString;
  currency: string;
  lot_code?: string;
  supplier?: string;
  observed_at?: IsoDateTime | null;
}

export interface CostView {
  currency: string;
  concentrate_g: DecimalString;
  total_minor: DecimalString | null;
  total_minor_rounded?: number;
  cost_per_kg_minor: DecimalString | null;
  complete: boolean;
  materials: {
    material: MaterialBrief;
    neat_g: DecimalString;
    price_per_neat_g_minor: DecimalString | null;
    cost_minor: DecimalString | null;
    share: DecimalString | null;
    source: PriceSource | null;
  }[];
  lines: { line_id: Uuid; label: string; cost_minor: DecimalString; complete: boolean }[];
  missing: MaterialBrief[];
  bottle: {
    bottle_ml: DecimalString;
    concentration: DecimalString;
    density: DecimalString;
    concentrate_g: DecimalString;
    cost_minor: DecimalString;
    complete: boolean;
    note: string;
  } | null;
  error: string | null;
}

export interface Requirements {
  concentrate_g: DecimalString;
  materials: {
    material: MaterialBrief;
    needed_neat_g: DecimalString;
    available_neat_g: DecimalString;
    shortfall_g: DecimalString;
  }[];
  error: string | null;
}

export type DiffStatusValue = "added" | "removed" | "changed" | "unchanged";

export interface LineForm {
  amount: DecimalString;
  unit: LineUnit;
  basis: LineBasis;
  active_fraction: DecimalString;
  carrier_material_id: Uuid | null;
  sub_version_label: string | null;
}

export interface VersionDiff {
  a: { id: Uuid; label: string; name: string; status: VersionStatus };
  b: { id: Uuid; label: string; name: string; status: VersionStatus };
  identical: boolean;
  error: string | null;
  materials: {
    material: MaterialBrief;
    status: DiffStatusValue;
    old_fraction: DecimalString | null;
    new_fraction: DecimalString | null;
    delta: DecimalString;
    form_changed: boolean;
  }[];
  carriers: VersionDiff["materials"];
  lines: { key: string; label: string; status: DiffStatusValue; before: LineForm | null; after: LineForm | null }[];
}

export type ScaleMode = "factor" | "total_g" | "concentration";

export interface ScaleRequest {
  mode: ScaleMode;
  factor?: DecimalString | null;
  total_g?: DecimalString | null;
  concentration?: DecimalString | null;
  batch_g?: DecimalString | null;
  create?: boolean;
  minor?: boolean;
}

export interface ScaleResult {
  mode: ScaleMode;
  rows: {
    line_id: Uuid | null;
    label: string;
    before: { amount: DecimalString; unit: LineUnit } | null;
    after: { amount: DecimalString; unit: LineUnit };
    weigh_g: DecimalString | null;
  }[];
  total_weigh_g: DecimalString | null;
  warnings: string[];
  version?: FormulaVersion;
}

export type DilutionMode = "keep_weight" | "keep_pct" | "exchange_solvent";

export interface ChangeDilutionRequest {
  line_id: Uuid;
  active_fraction: DecimalString;
  mode: DilutionMode;
  carrier_material_id?: Uuid | null;
  solvent_material_id?: Uuid | null;
  preview?: boolean;
}

export interface ChangeDilutionResult {
  mode: DilutionMode;
  applied: boolean;
  changes: {
    line_id: Uuid;
    label: string;
    before: { amount: DecimalString; unit: LineUnit; active_fraction: DecimalString; carrier_material_id: Uuid | null };
    after: { amount: DecimalString; unit: LineUnit; active_fraction: DecimalString; carrier_material_id: Uuid | null };
  }[];
  version: FormulaVersion;
}

export interface ParsedCandidate extends MaterialOption {
  score: DecimalString;
  method: "cas" | "short_code" | "name" | "synonym" | "fuzzy" | "store_title" | "link" | "carrier";
}

export interface ParsedLine {
  line_no: number;
  raw: string;
  name: string;
  amount: DecimalString | null;
  unit: LineUnit | null;
  basis: LineBasis;
  active_fraction: DecimalString | null;
  carrier_text: string | null;
  carrier_key: string | null;
  carrier: ParsedCandidate | null;
  note: string;
  /** A store's product page the line links to. */
  link: string;
  /** Listed without an amount in the chosen recipe ("–" or 0): not used. */
  absent: boolean;
  /** The amount in each of `ParseResult.amount_columns` (null: not used in that recipe). */
  amounts: (DecimalString | null)[];
  confidence: DecimalString;
  warnings: string[];
  status: "matched" | "ambiguous" | "unknown";
  /** Which stock settled a tie between equally good matches: the one in stock, else on order. */
  settled_by: "" | "in_stock" | "on_order";
  /** What to check before importing the line; empty when it is ready. */
  review: string[];
  match: ParsedCandidate | null;
  candidates: ParsedCandidate[];
}

/** One recipe of a pasted table with amount columns side by side. */
export interface AmountColumn {
  index: number;
  label: string;
  unit: LineUnit | null;
  total: DecimalString | null;
  used: number;
}

export interface ParseResult {
  format: "lines" | "table" | "csv" | "tsv";
  unit: string | null;
  total: DecimalString | null;
  stated_total: DecimalString | null;
  /** What to check before importing. */
  warnings: string[];
  /** How the text was read (units inferred, recipes found). */
  notes: string[];
  amount_columns: AmountColumn[];
  column: number | null;
  lines: ParsedLine[];
  summary: {
    lines: number;
    matched: number;
    ambiguous: number;
    unknown: number;
    unowned: number;
    on_order: number;
    ready: number;
    to_check: number;
    absent: number;
  };
}

export interface ImportRecipesRequest {
  recipes: { name: string; lines: LineInput[]; skipped?: string[] }[];
  project?: string;
  kind?: FormulaKind;
  tags?: string[];
  notes?: string;
}

export interface ImportParsedRequest {
  target: "new" | "version" | "replace";
  name?: string;
  kind?: FormulaKind;
  tags?: string[];
  notes?: string;
  formula_id?: Uuid | null;
  version_id?: Uuid | null;
  lines: LineInput[];
  skipped?: string[];
}

/** A material as the related view names it: the formula's own, else one in stock, else any. */
export interface RelatedMaterial {
  id: Uuid | null;
  name: string;
  kind: string;
  stock: StockState;
}

/** A formula of the workspace or of the published library. */
export type RelatedFormulaRef =
  | {
      kind: "workspace";
      id: Uuid;
      version_id: Uuid;
      name: string;
      code: string;
      label: string;
      project: string;
      formula_kind: FormulaKind;
    }
  | { kind: "published"; id: Uuid; name: string; source: string; style: string; smells_like: string; url: string }
  | { kind: "unknown"; id: string; name: string };

export interface SimilarFormula {
  formula: RelatedFormulaRef;
  /** Shared share plus half the share of alike materials (same odor slot); 1 = the same formula. */
  score: DecimalString;
  /** How alike the two formulas' odor-family profiles are (1 = the same), when both are known. */
  families: DecimalString | null;
  shared_share: DecimalString;
  alike_share: DecimalString;
  shared: { material: RelatedMaterial; here: DecimalString; there: DecimalString }[];
  alike: {
    here: RelatedMaterial;
    there: RelatedMaterial;
    slot: string;
    here_share: DecimalString;
    there_share: DecimalString;
  }[];
  only_here: RelatedMaterial[];
  only_there: RelatedMaterial[];
  only_there_count: number;
}

export interface AccordAlternative {
  material: RelatedMaterial;
  /** Formula groups using it with the rest of the accord, without the member. */
  groups: number;
  /** Formula groups with the rest of the accord and without the member. */
  contexts: number;
  lift: DecimalString;
  same_slot: boolean;
  /** Odor descriptors it shares with the member. */
  shared_terms: string[];
  in_formula: boolean;
}

export interface AccordMemberView {
  material: RelatedMaterial;
  /** Share of this formula. */
  share: DecimalString;
  /** Part of the accord, here. */
  part: DecimalString;
  typical_part: DecimalString | null;
  low_part: DecimalString | null;
  high_part: DecimalString | null;
  alternatives: AccordAlternative[];
}

export interface AccordView {
  members: AccordMemberView[];
  /** Share of this formula. */
  share: DecimalString;
  /** Formula groups (other than this formula's) using every member. */
  support: number;
  lift: DecimalString;
  use_count: number;
  uses: {
    formula: RelatedFormulaRef;
    share: DecimalString;
    /** 1 = the members in the same proportions as here. */
    closeness: DecimalString;
    same_group: boolean;
    parts: { material_id: Uuid | null; part: DecimalString }[];
  }[];
  companions: { material: RelatedMaterial; groups: number; frequency: DecimalString; lift: DecimalString }[];
}

export interface RelatedView {
  version: { id: Uuid; label: string; formula: { id: Uuid; code: string; name: string } };
  corpus: { formulas: number; workspace: number; published: number; groups: number };
  materials: number;
  similar: SimilarFormula[];
  accords: AccordView[];
}

/** A passage of a book in the workspace's library that mentions several of the materials asked about. */
export interface BookPassage {
  chunk_id: string;
  document_id: string;
  title: string;
  author: string;
  year: number | null;
  locator: string;
  segments: { text: string; match: boolean }[];
  /** Which of the materials asked about it mentions. */
  matched: Uuid[];
}

export interface ShareExport {
  format: "md" | "csv" | "txt";
  filename: string;
  mime: string;
  content: string;
}

export interface LotOption {
  id: Uuid;
  code: string;
  status: string;
  active_fraction: DecimalString;
  carrier: { id: Uuid; name: string } | null;
  balance_g: DecimalString | null;
}

export interface SubVersionOption {
  id: Uuid;
  label: string;
  status: VersionStatus;
  formula_id: Uuid;
  formula_code: string;
  formula_name: string;
  formula_kind: FormulaKind;
}

export interface FormulaListFilters {
  q?: string;
  kind?: FormulaKind;
  tag?: string;
  archived?: boolean;
  sort?: string;
  limit?: number;
  offset?: number;
}

export const formulasKeys = {
  all: ["formulas"] as const,
  list: (filters: FormulaListFilters) => ["formulas", "list", filters] as const,
  tags: () => ["formulas", "tags"] as const,
  formula: (id: Uuid) => ["formulas", "formula", id] as const,
  version: (id: Uuid) => ["formulas", "version", id] as const,
  normalized: (id: Uuid, batch: string | null, concentration: string | null) =>
    ["formulas", "version", id, "normalized", batch, concentration] as const,
  cost: (id: Uuid, params: Record<string, string | null>) => ["formulas", "version", id, "cost", params] as const,
  diff: (a: Uuid, b: Uuid) => ["formulas", "diff", a, b] as const,
  share: (id: Uuid, format: string) => ["formulas", "version", id, "share", format] as const,
  related: (id: Uuid) => ["formulas", "version", id, "related"] as const,
  lots: (materialId: Uuid) => ["formulas", "lots", materialId] as const,
};

// Reads --------------------------------------------------------------------------------------------

export function useFormulas(filters: FormulaListFilters) {
  return useQuery({
    queryKey: formulasKeys.list(filters),
    queryFn: ({ signal }) => apiGet<Paged<FormulaListItem>>("/formulas/", { params: { ...filters }, signal }),
    placeholderData: keepPreviousData,
  });
}

export function useFormulaTags() {
  return useQuery({
    queryKey: formulasKeys.tags(),
    queryFn: ({ signal }) => apiGet<string[]>("/formulas/tags", { signal }),
    staleTime: 60_000,
  });
}

export function useFormula(id: Uuid | undefined) {
  return useQuery({
    queryKey: formulasKeys.formula(id ?? ""),
    queryFn: ({ signal }) => apiGet<Formula>(`/formulas/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useFormulaVersion(id: Uuid | null | undefined) {
  return useQuery({
    queryKey: formulasKeys.version(id ?? ""),
    queryFn: ({ signal }) => apiGet<FormulaVersion>(`/formulas/versions/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useNormalized(id: Uuid | null | undefined, batchG: string | null, concentration: string | null) {
  return useQuery({
    queryKey: formulasKeys.normalized(id ?? "", batchG, concentration),
    queryFn: ({ signal }) =>
      apiGet<NormalizedView>(`/formulas/versions/${id}/normalized`, {
        params: { batch_g: batchG, concentration },
        signal,
      }),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

export function useCost(id: Uuid | null | undefined, params: Record<string, string | null>) {
  return useQuery({
    queryKey: formulasKeys.cost(id ?? "", params),
    queryFn: ({ signal }) => apiGet<CostView>(`/formulas/versions/${id}/cost`, { params, signal }),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
  });
}

export function useVersionDiff(a: Uuid | null, b: Uuid | null) {
  return useQuery({
    queryKey: formulasKeys.diff(a ?? "", b ?? ""),
    queryFn: ({ signal }) => apiGet<VersionDiff>("/formulas/diff", { params: { a, b }, signal }),
    enabled: Boolean(a && b),
  });
}

/** Formulas like this version and the accords it shares with them (computed on the server, cached a while). */
export function useRelated(id: Uuid | null | undefined) {
  return useQuery({
    queryKey: formulasKeys.related(id ?? ""),
    queryFn: ({ signal }) => apiGet<RelatedView>(`/formulas/versions/${id}/related`, { signal }),
    enabled: Boolean(id),
    staleTime: 5 * 60_000,
  });
}

/** Passages of the library's books about at least two of these materials together (an accord, a substitute). */
export function useBookPassages(materialIds: ReadonlyArray<Uuid | null>, limit = 4) {
  const ids = [...new Set(materialIds.filter((id): id is Uuid => Boolean(id)))].slice(0, 12);
  return useQuery({
    queryKey: ["formulas", "book-passages", ids, limit] as const,
    queryFn: ({ signal }) =>
      apiGet<{ items: BookPassage[]; count: number }>("/assistant/library/passages", {
        params: { material: ids, at_least: 2, limit },
        signal,
      }),
    enabled: ids.length >= 2,
    staleTime: 5 * 60_000,
  });
}

export function useShareExport(id: Uuid | null, format: ShareExport["format"], enabled: boolean) {
  return useQuery({
    queryKey: formulasKeys.share(id ?? "", format),
    queryFn: ({ signal }) => apiGet<ShareExport>(`/formulas/versions/${id}/share`, { params: { format }, signal }),
    enabled: Boolean(id) && enabled,
  });
}

export function useLotOptions(materialId: Uuid | null | undefined) {
  return useQuery({
    queryKey: formulasKeys.lots(materialId ?? ""),
    queryFn: ({ signal }) =>
      apiGet<LotOption[]>("/formulas/lot-options", { params: { material_id: materialId }, signal }),
    enabled: Boolean(materialId),
  });
}

/** Combobox loader for line materials (name, synonym, short code or CAS; owned first). */
export function searchMaterials(query: string, signal: AbortSignal): Promise<MaterialOption[]> {
  return apiGet<MaterialOption[]>("/formulas/material-options", { params: { q: query, limit: 20 }, signal });
}

/** Combobox loader for accords (versions of other formulas that can be nested). */
export function searchSubVersions(
  query: string,
  signal: AbortSignal,
  excludeFormula?: Uuid,
): Promise<SubVersionOption[]> {
  return apiGet<SubVersionOption[]>("/formulas/sub-version-options", {
    params: { q: query, exclude_formula: excludeFormula },
    signal,
  });
}

/** Combobox loader for formulas (used by the compliance page). */
export async function searchFormulas(query: string, signal: AbortSignal): Promise<FormulaListItem[]> {
  const page = await apiGet<Paged<FormulaListItem>>("/formulas/", { params: { q: query, limit: 20 }, signal });
  return page.items;
}

export function fetchRequirements(id: Uuid, concentrateG: string): Promise<Requirements> {
  return apiGet<Requirements>(`/formulas/versions/${id}/requirements`, { params: { concentrate_g: concentrateG } });
}

// Writes -------------------------------------------------------------------------------------------

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: formulasKeys.all });
}

export interface FormulaCreate {
  name: string;
  kind: FormulaKind;
  tags?: string[];
  family?: string;
  project?: string;
  notes?: string;
  lines?: LineInput[];
}

export function useCreateFormula() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: FormulaCreate) => apiPost<Formula>("/formulas/", body),
    onSettled: invalidate,
  });
}

export function useUpdateFormula(id: Uuid) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (
      body: Partial<Pick<Formula, "name" | "kind" | "tags" | "family" | "project" | "notes" | "archived">>,
    ) => apiPatch<Formula>(`/formulas/${id}`, body),
    onSettled: invalidate,
  });
}

export function useVersionAction(versionId: Uuid | null) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ action, body }: { action: "release" | "lock" | "new-version"; body?: unknown }) =>
      apiPost<FormulaVersion>(`/formulas/versions/${versionId}/${action}`, body ?? {}),
    onSuccess: (version) => queryClient.setQueryData(formulasKeys.version(version.id), version),
    onSettled: invalidate,
  });
}

export function useDeleteVersion() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (versionId: Uuid) => apiDelete(`/formulas/versions/${versionId}`),
    onSettled: invalidate,
  });
}

export function useUpdateVersionNotes(versionId: Uuid | null) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (notes: string) => apiPatch<FormulaVersion>(`/formulas/versions/${versionId}`, { notes }),
    onSettled: invalidate,
  });
}

/** Line edits on a draft; each returns the updated version, which replaces the cached one. */
export function useLineMutations(versionId: Uuid | null) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  const onSuccess = (version: FormulaVersion) => queryClient.setQueryData(formulasKeys.version(version.id), version);
  const base = `/formulas/versions/${versionId}/lines`;
  return {
    add: useMutation({
      mutationFn: (line: LineInput & { position?: number }) => apiPost<FormulaVersion>(base, line),
      onSuccess,
      onSettled: invalidate,
    }),
    update: useMutation({
      mutationFn: ({ lineId, patch }: { lineId: Uuid; patch: LinePatch }) =>
        apiPatch<FormulaVersion>(`${base}/${lineId}`, patch),
      onMutate: async ({ lineId, patch }) => {
        if (!versionId) return undefined;
        await queryClient.cancelQueries({ queryKey: formulasKeys.version(versionId) });
        const previous = queryClient.getQueryData<FormulaVersion>(formulasKeys.version(versionId));
        if (previous) {
          const { amount, unit, flag, notes } = patch;
          queryClient.setQueryData<FormulaVersion>(formulasKeys.version(versionId), {
            ...previous,
            lines: previous.lines.map((line) =>
              line.id === lineId
                ? {
                    ...line,
                    ...(amount !== undefined ? { amount } : {}),
                    ...(unit !== undefined ? { unit } : {}),
                    ...(flag !== undefined ? { flag } : {}),
                    ...(notes !== undefined ? { notes } : {}),
                  }
                : line,
            ),
          });
        }
        return { previous };
      },
      onError: (_error, _variables, context) => {
        if (versionId && context?.previous) queryClient.setQueryData(formulasKeys.version(versionId), context.previous);
      },
      onSuccess,
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: (lineId: Uuid) => apiDelete<FormulaVersion>(`${base}/${lineId}`),
      onSuccess,
      onSettled: invalidate,
    }),
    reorder: useMutation({
      mutationFn: (lineIds: Uuid[]) => apiPost<FormulaVersion>(`${base}/reorder`, { line_ids: lineIds }),
      onSuccess,
      onSettled: invalidate,
    }),
    replace: useMutation({
      mutationFn: (lines: LineInput[]) => apiPut<FormulaVersion>(base, { lines }),
      onSuccess,
      onSettled: invalidate,
    }),
  };
}

export function useScale(versionId: Uuid | null) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ScaleRequest) => apiPost<ScaleResult>(`/formulas/versions/${versionId}/scale`, body),
    onSettled: (result) => (result?.version ? invalidate() : undefined),
  });
}

export function useChangeDilution(versionId: Uuid | null) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ChangeDilutionRequest) =>
      apiPost<ChangeDilutionResult>(`/formulas/versions/${versionId}/change-dilution`, body),
    onSuccess: (result) => {
      if (result.applied) queryClient.setQueryData(formulasKeys.version(result.version.id), result.version);
    },
    onSettled: (result) => (result?.applied ? invalidate() : undefined),
  });
}

export function useParseFormula() {
  return useMutation({ mutationFn: (text: string) => apiPost<ParseResult>("/formulas/parse", { text }) });
}

export interface ImportedFormula {
  formula_id: Uuid;
  version_id: Uuid;
  label: string;
}

export function useImportParsed() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ImportParsedRequest) => apiPost<ImportedFormula>("/formulas/import-parsed", body),
    onSettled: invalidate,
  });
}

/** Recipes pasted side by side, each as its own formula. */
export function useImportRecipes() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ImportRecipesRequest) => apiPost<ImportedFormula[]>("/formulas/import-recipes", body),
    onSettled: invalidate,
  });
}

/** "Buy missing": a draft basket in purchasing from neat shortfalls. */
export function createBasket(body: {
  name: string;
  lines: { material_id: Uuid; neat_g: DecimalString }[];
  source?: Record<string, string>;
}) {
  return apiPost<{ id: Uuid }>("/purchasing/baskets", body);
}
